import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, useRouteError, isRouteErrorResponse } from 'react-router-dom'
import { AppLayout } from '@/layouts/AppLayout'
import { AuthLayout } from '@/layouts/AuthLayout'
import { POSLayout } from '@/layouts/POSLayout'
import { LandingLayout } from '@/layouts/LandingLayout'
import { ProtectedRoute } from './ProtectedRoute'
import { AdminRoute } from './AdminRoute'
import { Spinner } from '@/components/ui/Spinner'
import { AlertTriangle, RefreshCw } from 'lucide-react'

function RouteErrorPage() {
  const error = useRouteError()
  let message = 'Beklenmeyen bir hata oluştu.'
  let status: number | undefined

  if (isRouteErrorResponse(error)) {
    status = error.status
    message = error.statusText || error.data?.message || message
  } else if (error instanceof Error) {
    message = error.message
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center bg-bg-base">
      <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center mb-4">
        <AlertTriangle className="w-8 h-8 text-error" />
      </div>
      <h2 className="text-2xl font-bold text-text-primary mb-2">
        {status ? `${status} — ` : ''}Bir hata oluştu
      </h2>
      <p className="text-sm text-text-secondary max-w-md mb-6">{message}</p>
      <div className="flex gap-3">
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Sayfayı Yenile
        </button>
        <button
          onClick={() => (window.location.href = '/')}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-bg-elevated text-text-primary hover:bg-bg-border transition-colors"
        >
          Ana Sayfaya Dön
        </button>
      </div>
    </div>
  )
}

// Lazy loaded pages
const LandingPage = lazy(() => import('@/modules/landing/pages/LandingPage'))
const FiyatlandirmaPage = lazy(() => import('@/modules/landing/pages/FiyatlandirmaPage'))
const CheckoutPage = lazy(() => import('@/modules/landing/pages/CheckoutPage'))
const DenemeKayitPage = lazy(() => import('@/modules/landing/pages/DenemeKayitPage'))

const LoginPage = lazy(() => import('@/modules/auth/pages/LoginPage'))
const FirmaKayitPage = lazy(() => import('@/modules/auth/pages/FirmaKayitPage'))
const SifreSifirlamaPage = lazy(() => import('@/modules/auth/pages/SifreSifirlamaPage'))
const YeniSifrePage = lazy(() => import('@/modules/auth/pages/YeniSifrePage'))
const EmailDogrulamaPage = lazy(() => import('@/modules/auth/pages/EmailDogrulamaPage'))
const POSPage = lazy(() => import('@/modules/pos/pages/POSPage'))
const DashboardPage = lazy(() => import('@/modules/dashboard/pages/DashboardPage'))
const StokListePage = lazy(() => import('@/modules/stok/pages/StokListePage'))
const StokKartPage = lazy(() => import('@/modules/stok/pages/StokKartPage'))
const StokHareketleriPage = lazy(() => import('@/modules/stok/pages/StokHareketleriPage'))
const StokSayimPage = lazy(() => import('@/modules/stok/pages/StokSayimPage'))
const CariListePage = lazy(() => import('@/modules/cari/pages/CariListePage'))
const CariKartPage = lazy(() => import('@/modules/cari/pages/CariKartPage'))
const CariHareketleriPage = lazy(() => import('@/modules/cari/pages/CariHareketleriPage'))
const FaturaListePage = lazy(() => import('@/modules/fatura/pages/FaturaListePage'))
const FaturaOlusturPage = lazy(() => import('@/modules/fatura/pages/FaturaOlusturPage'))
const FaturaTipListePage = lazy(() => import('@/modules/fatura/pages/FaturaTipListePage'))
const FaturaTipOlusturPage = lazy(() => import('@/modules/fatura/pages/FaturaTipOlusturPage'))
const KasaPage = lazy(() => import('@/modules/kasa/pages/KasaPage'))
const BankaPage = lazy(() => import('@/modules/banka/pages/BankaPage'))
const CekListePage = lazy(() => import('@/modules/cek/pages/CekListePage'))
const TaksitListesiPage = lazy(() => import('@/modules/taksit/pages/TaksitListesiPage'))
const TaksitPlaniDetayPage = lazy(() => import('@/modules/taksit/pages/TaksitPlaniDetayPage'))
const SiparisListePage = lazy(() => import('@/modules/siparis/pages/SiparisListePage'))
const SiparisOlusturPage = lazy(() => import('@/modules/siparis/pages/SiparisOlusturPage'))
const RaporlarPage = lazy(() => import('@/modules/raporlar/pages/RaporlarPage'))
const RaporBuilderGaleriPage = lazy(() => import('@/modules/raporlar/pages/builder/GaleriPage'))
const ViewOlusturPage = lazy(() => import('@/modules/raporlar/pages/builder/ViewOlusturPage'))
const RaporOlusturPage = lazy(() => import('@/modules/raporlar/pages/builder/RaporOlusturPage'))
const RaporCalistirPage = lazy(() => import('@/modules/raporlar/pages/builder/RaporCalistirPage'))

// Ayarlar
const AyarlarPage = lazy(() => import('@/modules/ayarlar/pages/AyarlarPage'))
const FirmaBilgileriPage = lazy(() => import('@/modules/ayarlar/pages/FirmaBilgileriPage'))
const KullanicilarPage = lazy(() => import('@/modules/ayarlar/pages/KullanicilarPage'))
const BelgeSerileriPage = lazy(() => import('@/modules/ayarlar/pages/BelgeSerileriPage'))
const OlcuBirimleriPage = lazy(() => import('@/modules/ayarlar/pages/OlcuBirimleriPage'))
const TevkifatAyarlariPage = lazy(() => import('@/modules/ayarlar/pages/TevkifatAyarlariPage'))
const ProfilPage = lazy(() => import('@/modules/ayarlar/pages/ProfilPage'))
const KodTanimlamaPage = lazy(() => import('@/modules/ayarlar/pages/KodTanimlamaPage'))
const GrupKodTanimlamaPage = lazy(() => import('@/modules/ayarlar/pages/GrupKodTanimlamaPage'))
const HizliSatisAyarlariPage = lazy(() => import('@/modules/ayarlar/pages/HizliSatisAyarlariPage'))
const RehberAyarlariPage = lazy(() => import('@/modules/ayarlar/pages/RehberAyarlariPage'))
const VeriAktarimPage = lazy(() => import('@/modules/ayarlar/pages/VeriAktarimPage'))
const EtiketAyarlariPage = lazy(() => import('@/modules/ayarlar/pages/EtiketAyarlariPage'))
const AbonelikPage = lazy(() => import('@/modules/ayarlar/pages/AbonelikPage'))
const DenetimKayitPage = lazy(() => import('@/modules/ayarlar/pages/DenetimKayitPage'))
const HesapKapatmaPage = lazy(() => import('@/modules/ayarlar/pages/HesapKapatmaPage'))
const PosSatisGecmisiPage = lazy(() => import('@/modules/pos/pages/PosSatisGecmisiPage'))
const FaturalarimPage = lazy(() => import('@/modules/ayarlar/pages/FaturalarimPage'))
const VeriIndirPage = lazy(() => import('@/modules/ayarlar/pages/VeriIndirPage'))
const BillingSuccessPage = lazy(() => import('@/modules/billing/pages/BillingSuccessPage'))
const BillingCancelPage = lazy(() => import('@/modules/billing/pages/BillingCancelPage'))
const AdminFirmaListesiPage = lazy(() => import('@/modules/admin/pages/AdminFirmaListesiPage'))

// Depo modülü
const DepoTransferListePage = lazy(() => import('@/modules/depo/pages/DepoTransferListePage'))
const DepoTransferOlusturPage = lazy(() => import('@/modules/depo/pages/DepoTransferOlusturPage'))

// Ayarlar - Şube & Depo
const SubelerPage = lazy(() => import('@/modules/ayarlar/pages/SubelerPage'))
const DepolarPage = lazy(() => import('@/modules/ayarlar/pages/DepolarPage'))
const AdminDashboardPage = lazy(() => import('@/modules/admin/pages/AdminDashboardPage'))
const AdminPlanlarPage = lazy(() => import('@/modules/admin/pages/AdminPlanlarPage'))
const AdminYedeklerPage = lazy(() => import('@/modules/admin/pages/AdminYedeklerPage'))

// Desktop (Tauri) sayfaları
const LicensePage = lazy(() => import('@/modules/desktop/LicensePage'))
const SetupWizard = lazy(() => import('@/modules/desktop/SetupWizard'))

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <Spinner size="lg" className="text-primary" />
    </div>
  )
}

function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const router = createBrowserRouter([
  // Landing routes
  {
    element: <LandingLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/', element: <S><LandingPage /></S> },
      { path: '/fiyatlandirma', element: <S><FiyatlandirmaPage /></S> },
      { path: '/checkout/:plan', element: <S><CheckoutPage /></S> },
      { path: '/deneme-kayit', element: <S><DenemeKayitPage /></S> },
    ],
  },

  // Desktop-only routes (Tauri)
  { path: '/activate', element: <S><LicensePage /></S> },
  { path: '/setup', element: <S><SetupWizard /></S> },

  // Auth routes
  {
    element: <AuthLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/login', element: <S><LoginPage /></S> },
      { path: '/kayit', element: <S><FirmaKayitPage /></S> },
      { path: '/sifre-sifirlama', element: <S><SifreSifirlamaPage /></S> },
      { path: '/yeni-sifre', element: <S><YeniSifrePage /></S> },
      { path: '/email-dogrulama', element: <S><EmailDogrulamaPage /></S> },
    ],
  },

  // POS (full-screen, separate layout)
  {
    element: <ProtectedRoute><POSLayout /></ProtectedRoute>,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/pos', element: <S><POSPage /></S> },
    ],
  },

  // Protected app routes
  {
    element: <ProtectedRoute><AppLayout /></ProtectedRoute>,
    errorElement: <RouteErrorPage />,
    children: [
      { path: 'dashboard', element: <S><DashboardPage /></S> },

      // Stok (sabit route'lar :id'den önce)
      { path: 'stok', element: <S><StokListePage /></S> },
      { path: 'stok/hareketler', element: <S><StokHareketleriPage /></S> },
      { path: 'stok/sayim', element: <S><StokSayimPage /></S> },
      { path: 'stok/yeni', element: <S><StokKartPage mode="create" /></S> },
      { path: 'stok/:id', element: <S><StokKartPage mode="view" /></S> },
      { path: 'stok/:id/duzenle', element: <S><StokKartPage mode="edit" /></S> },

      // Cari (sabit route'lar :id'den önce)
      { path: 'cari', element: <S><CariListePage /></S> },
      { path: 'cari/hareketler', element: <S><CariHareketleriPage /></S> },
      { path: 'cari/yeni', element: <S><CariKartPage mode="create" /></S> },
      { path: 'cari/:id', element: <S><CariKartPage mode="view" /></S> },
      { path: 'cari/:id/duzenle', element: <S><CariKartPage mode="edit" /></S> },

      // Fatura (sabit route'lar :id'den önce)
      { path: 'fatura', element: <S><FaturaListePage /></S> },
      { path: 'fatura/yeni', element: <S><FaturaOlusturPage /></S> },
      { path: 'fatura/alis-irsaliye',       element: <S><FaturaTipListePage tipKey="alis-irsaliye" /></S> },
      { path: 'fatura/alis-irsaliye/yeni',  element: <S><FaturaTipOlusturPage tipKey="alis-irsaliye" /></S> },
      { path: 'fatura/satis-irsaliye',      element: <S><FaturaTipListePage tipKey="satis-irsaliye" /></S> },
      { path: 'fatura/satis-irsaliye/yeni', element: <S><FaturaTipOlusturPage tipKey="satis-irsaliye" /></S> },
      { path: 'fatura/alis',                element: <S><FaturaTipListePage tipKey="alis" /></S> },
      { path: 'fatura/alis/yeni',           element: <S><FaturaTipOlusturPage tipKey="alis" /></S> },
      { path: 'fatura/satis',               element: <S><FaturaTipListePage tipKey="satis" /></S> },
      { path: 'fatura/satis/yeni',          element: <S><FaturaTipOlusturPage tipKey="satis" /></S> },
      { path: 'fatura/alis-iade',           element: <S><FaturaTipListePage tipKey="alis-iade" /></S> },
      { path: 'fatura/alis-iade/yeni',      element: <S><FaturaTipOlusturPage tipKey="alis-iade" /></S> },
      { path: 'fatura/satis-iade',          element: <S><FaturaTipListePage tipKey="satis-iade" /></S> },
      { path: 'fatura/satis-iade/yeni',     element: <S><FaturaTipOlusturPage tipKey="satis-iade" /></S> },
      // Diğer modüller
      { path: 'kasa', element: <S><KasaPage /></S> },
      { path: 'kasa/hareketler', element: <Navigate to="/kasa" replace /> },
      { path: 'banka', element: <S><BankaPage /></S> },
      { path: 'banka/hareketler', element: <Navigate to="/banka" replace /> },
      { path: 'cek', element: <S><CekListePage /></S> },
      { path: 'taksit', element: <S><TaksitListesiPage /></S> },
      { path: 'taksit/:planId', element: <S><TaksitPlaniDetayPage /></S> },
      { path: 'siparis',                        element: <S><SiparisListePage tip="satis" /></S> },
      { path: 'siparis/yeni',                    element: <S><SiparisOlusturPage tip="satis" /></S> },
      { path: 'siparis/satin-alma',              element: <S><SiparisListePage tip="alis" /></S> },
      { path: 'siparis/satin-alma/yeni',         element: <S><SiparisOlusturPage tip="alis" /></S> },
      { path: 'siparis/satin-alma/:id',          element: <S><SiparisOlusturPage tip="alis" /></S> },
      { path: 'siparis/satin-alma/:id/duzenle',  element: <S><SiparisOlusturPage tip="alis" editMode /></S> },
      { path: 'siparis/:id/duzenle',             element: <S><SiparisOlusturPage tip="satis" editMode /></S> },
      { path: 'siparis/:id',                     element: <S><SiparisOlusturPage tip="satis" /></S> },
      { path: 'hizli-satis/gecmis', element: <S><PosSatisGecmisiPage /></S> },
      { path: 'raporlar', element: <S><RaporlarPage /></S> },

      // Rapor Builder
      { path: 'raporlar/builder', element: <S><RaporBuilderGaleriPage /></S> },
      { path: 'raporlar/builder/view/yeni', element: <S><ViewOlusturPage /></S> },
      { path: 'raporlar/builder/view/:id', element: <S><ViewOlusturPage /></S> },
      { path: 'raporlar/builder/rapor/yeni', element: <S><RaporOlusturPage /></S> },
      { path: 'raporlar/builder/rapor/:id', element: <S><RaporOlusturPage /></S> },
      { path: 'raporlar/builder/calistir/:raporId', element: <S><RaporCalistirPage /></S> },

      // Depo Transfer
      { path: 'depo/transfer', element: <S><DepoTransferListePage /></S> },
      { path: 'depo/transfer/yeni', element: <S><DepoTransferOlusturPage /></S> },
      { path: 'depo/transfer/:id', element: <S><DepoTransferOlusturPage /></S> },
      { path: 'depo/transfer/:id/duzenle', element: <S><DepoTransferOlusturPage editMode /></S> },

      // Ayarlar (nested)
      {
        path: 'ayarlar',
        element: <S><AyarlarPage /></S>,
        children: [
          { path: 'profil', element: <S><ProfilPage /></S> },
          { path: 'firma', element: <S><FirmaBilgileriPage /></S> },
          { path: 'kullanicilar', element: <S><KullanicilarPage /></S> },
          { path: 'belge-serileri', element: <S><BelgeSerileriPage /></S> },
          { path: 'olcu-birimleri', element: <S><OlcuBirimleriPage /></S> },
          { path: 'tevkifat', element: <S><TevkifatAyarlariPage /></S> },
          { path: 'kod-tanimlama', element: <S><KodTanimlamaPage /></S> },
          { path: 'grup-kod-tanimlama', element: <S><GrupKodTanimlamaPage /></S> },
          { path: 'hizli-satis', element: <S><HizliSatisAyarlariPage /></S> },
          { path: 'rehber-ayarlari', element: <S><RehberAyarlariPage /></S> },
          { path: 'veri-aktarim', element: <S><VeriAktarimPage /></S> },
          { path: 'etiket', element: <S><EtiketAyarlariPage /></S> },
          { path: 'abonelik', element: <S><AbonelikPage /></S> },
          { path: 'faturalarim', element: <S><FaturalarimPage /></S> },
          { path: 'veri-indir', element: <S><VeriIndirPage /></S> },
          { path: 'denetim-kayit', element: <S><DenetimKayitPage /></S> },
          { path: 'hesap-kapatma', element: <S><HesapKapatmaPage /></S> },
          { path: 'subeler', element: <S><SubelerPage /></S> },
          { path: 'depolar', element: <S><DepolarPage /></S> },
        ],
      },

      // Billing (Stripe sonuç sayfaları — AppLayout içinde ama tam ekran gibi)
      { path: 'billing/success', element: <S><BillingSuccessPage /></S> },
      { path: 'billing/cancel', element: <S><BillingCancelPage /></S> },

      // Admin
      { path: 'admin', element: <AdminRoute><S><AdminDashboardPage /></S></AdminRoute> },
      { path: 'admin/firmalar', element: <AdminRoute><S><AdminFirmaListesiPage /></S></AdminRoute> },
      { path: 'admin/planlar', element: <AdminRoute><S><AdminPlanlarPage /></S></AdminRoute> },
      { path: 'admin/yedekler', element: <AdminRoute><S><AdminYedeklerPage /></S></AdminRoute> },

      // 404
      { path: '*', element: <Navigate to="/dashboard" replace /> },
    ],
  },
])
